/**
 * Copyright ToolBelt Data Inc., 2021 All Rights Reserved
 */

import React from "react";
import styled from "styled-components";
import ComboBox from "../../../../../common/components/ComboBox";
import InsightBox from "../InsightBox";
import {APP_COLORS} from "../../../../../common/Constants";

const ExportText = styled.span`
  color: ${APP_COLORS['orange']};
  font-weight: 500;
`;

const MatchText = styled.span`
  color: ${APP_COLORS['green']};
  font-weight: 500;
`;

// LISTS filter (CONTAINED_IN / NOT_CONTAINED_IN) options carry the list's
// type (e.g. MATCH_COMPANY, EXPORT_CONTACT) so we can flag which lists came
// from a match result vs. an export, matching the (M)/(E) indicator the
// legacy CompanyFilter/ContactFilter combobox used to show.
const showMatchAndExport = (type) => {
    if (!type) return null;
    if (type.includes('MATCH')) return <MatchText>(M)</MatchText>;
    if (type.includes('EXPORT')) return <ExportText>(E)</ExportText>;
    return null;
};

const LIST_TYPE_BADGE_FILTER_IDS = ['CONTAINED_IN', 'NOT_CONTAINED_IN'];

// Range-bucket filters (Employees, Sales, Years in Business) come back from
// the filters-metadata endpoint in whatever order the backend returns them —
// not necessarily numeric. Pull the leading number out of the option's label
// (falling back to its id) so "1-10, 11-50, 51-200, ...1000+" sorts correctly
// instead of alphabetically ("1-10, 1000+, 11-50, ..."). Unparseable labels
// sort last rather than throwing.
const extractLeadingNumber = (value) => {
    if (!value) return Number.POSITIVE_INFINITY;
    const match = String(value).replace(/[,$]/g, '').match(/\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : Number.POSITIVE_INFINITY;
};

const sortNumerically = (opts) => [...opts].sort((a, b) =>
    extractLeadingNumber(a.display ?? a.id) - extractLeadingNumber(b.display ?? b.id)
);

// Employees/Sales/Years in Business ids carry their bucket rank as a leading
// letter (e.g. "A:500K", "B:500K-1M", ... "I:100M+") — that's the authoritative
// order, unlike sortNumerically's leading-number extraction, which breaks
// whenever adjacent buckets cross a K/M unit boundary (e.g. "500K" vs "1M"
// both start with digits that compare wrong as raw numbers: 500 > 1). Ids
// without a letter prefix (e.g. the "NULL" / "Year not specified" bucket)
// fall back to sorting on the raw id, which reliably lands them last after
// any single-letter prefix.
const extractIdPrefix = (id) => {
    const match = String(id ?? '').match(/^([A-Za-z]+):/);
    return match ? match[1] : String(id ?? '');
};

const sortByIdPrefix = (opts) => [...opts].sort((a, b) =>
    extractIdPrefix(a.id).localeCompare(extractIdPrefix(b.id))
);

// The filters-metadata endpoint's backing query has no ORDER BY at all (every
// meta_key's option array comes back in whatever order Snowflake happens to
// return, effectively nondeterministic) — confirmed there's no sort clause in
// atlas-api's get-product-metadata query. So every categorical dropdown gets
// alphabetized by default here; a filter opts into `sortMode: 'numeric'` for
// range buckets (Employees/Sales/Years in Business), or `sortMode: 'none'` to
// preserve whatever order the backend returns, if some future filter needs it.
const sortAlphabetically = (opts) => [...opts].sort((a, b) =>
    String(a.display ?? a.id ?? '').localeCompare(String(b.display ?? b.id ?? ''), undefined, {numeric: true, sensitivity: 'base'})
);

const FilterComboBox = (props) => {
    const {
        ftOption,
        stateObj,
        metaDataObj,
        handleKeyValueChange,
        handleSubmitFilter,
        handleRemoveFilter,
        productSubscription
    } = props;

    if (!ftOption) return null;

    const {
        id,
        label,
        metaDataId,
        disabled,
        multiple = true,
        insight,
        isLocked,
        preview,
        infoText,
        schemaProperty,
        optionsSchemaProperty,
        sortMode,
        allowNew,
        keepOpen
    } = ftOption;

    const targetKey = metaDataId || id;

    // Dropdown options are looked up by schemaProperty by default, but a filter
    // can point its options at a different metadata key via optionsSchemaProperty
    // (e.g. PRIMARY_TRADE/SECONDARY_TRADES both source the shared
    // `company_class_trades` list while keeping distinct cube filter columns).
    let options = metaDataObj?.[optionsSchemaProperty || schemaProperty] || [];

    if (options.length > 0 && sortMode !== 'none') {
        if (sortMode === 'idPrefix') {
            options = sortByIdPrefix(options);
        } else if (sortMode === 'numeric') {
            options = sortNumerically(options);
        } else {
            options = sortAlphabetically(options);
        }
    }

    const isLoading = !!metaDataObj?.isLoading;
    // allowNew fields (free-typed values, e.g. postal codes) have no
    // predefined option list at all — an empty array is their normal steady
    // state, not "nothing to select from", so it must not disable the field.
    const isOptionsEmpty = !allowNew && !isLoading && options.length === 0;
    const restrictions = productSubscription?.restrictions;

    if (restrictions && options.length > 0) {
        if ((id === 'PRIMARY_TRADE' || id === 'SECONDARY_TRADES') && Array.isArray(restrictions.tradeClassifications)) {
            options = options.filter(opt =>
                restrictions.tradeClassifications.includes(opt.id) ||
                restrictions.tradeClassifications.includes(opt.display)
            );
        }

        else if (id === 'MSA' && Array.isArray(restrictions.metropolitanStatisticalAreas)) {
            options = options.filter(opt =>
                restrictions.metropolitanStatisticalAreas.includes(opt.id) ||
                restrictions.metropolitanStatisticalAreas.includes(opt.display)
            );
        }

        else if (id === 'STATE' && Array.isArray(restrictions.regions)) {
            options = options.filter(opt =>
                restrictions.regions.includes(opt.id) ||
                restrictions.regions.includes(opt.display)
            );
        }
    }

    let selectedValue = [];
    const rawStateValue = stateObj?.[id];

    if (rawStateValue) {
        let actualValue = rawStateValue;
        if (typeof rawStateValue === 'object' && !Array.isArray(rawStateValue)) {
            if (rawStateValue.value !== undefined) {
                actualValue = rawStateValue.value;
            }
        }

        let tempArray = [];
        if (Array.isArray(actualValue)) {
            tempArray = actualValue;
        } else if (typeof actualValue === 'string') {
            try {
                const parsed = JSON.parse(actualValue);
                tempArray = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
                tempArray = [actualValue];
            }
        } else if (actualValue) {
            tempArray = [actualValue];
        }

        selectedValue = tempArray.map(item => {
            if (typeof item === 'string') {
                const matchedOption = options.find(opt => String(opt.id) === item || String(opt.display) === item);
                return matchedOption ? { ...matchedOption } : { id: item, display: item };
            }

            if (typeof item === 'object' && item !== null) {
                return {
                    ...item,
                    id: String(item.id || item.display || ''),
                    display: String(item.display || item.id || '')
                };
            }
            return null;
        }).filter(item => item !== null && item.id !== id);
    }

    const handleChange = (value) => {
        handleKeyValueChange(id, value);

        if (!value || value.length === 0) {
            handleRemoveFilter({ ...ftOption });
        } else {
            handleSubmitFilter({
                ...ftOption,
                value: value
            });
        }
    };

    let placeholderText = allowNew ? "Type a value and press enter..." : "Please select...";
    if (isLoading) {
        placeholderText = "Loading...";
    } else if (isOptionsEmpty) {
        placeholderText = "No options available";
    }

    const renderMenuItemChildren = LIST_TYPE_BADGE_FILTER_IDS.includes(id)
        ? (option) => (
            <div key={option.id} style={{display: 'flex', justifyContent: 'space-between'}}>
                <span>{showMatchAndExport(option.type)}&nbsp;{option.display}</span>
            </div>
        )
        : undefined;

    return (
        <div>
            <ComboBox
                id={id}
                label={label}
                placeholder={placeholderText}
                disabled={!!disabled || isLocked || isLoading || isOptionsEmpty}
                multiple={multiple}
                isLoading={isLoading}
                options={options}
                selected={selectedValue}
                onChange={handleChange}
                clearButton
                lockIcon={isLocked}
                isPreview={preview}
                infoText={infoText}
                renderMenuItemChildren={renderMenuItemChildren}
                allowNew={allowNew}
                keepOpen={keepOpen}
            />
            {insight && (
                <div style={{ marginTop: '8px' }}>
                    <InsightBox
                        text={insight.text}
                        title={insight.title}
                    />
                </div>
            )}
        </div>
    );
}

export default FilterComboBox;